import React, { useState, useEffect } from 'react';
import { 
  FaUniversity, FaUsers, FaGraduationCap, FaCalendarAlt, FaChartBar, 
  FaSearch, FaEdit, FaEye, FaDownload, FaUpload, FaSignOutAlt, 
  FaTimes, FaBars, FaPlus, FaTrash, FaUserGraduate, FaBuilding,
  FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe, FaStar
} from 'react-icons/fa';
import { toast, Toaster } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function InstitutionDashboard({ onLogout }) {
  const [institution, setInstitution] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Data states
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [visits, setVisits] = useState([]);
  const [placementBatches, setPlacementBatches] = useState([]);
  const [candidates, setCandidates] = useState([]);

  // Pagination states
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [studentsLimit] = useState(10);
  const [studentsSearch, setStudentsSearch] = useState('');
  const [statistics, setStatistics] = useState({});

  // Modal states
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // CSV Upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Form states
  const [courseForm, setCourseForm] = useState({
    course_name: '',
    trade_code: '',
    duration_months: '',
    total_seats: '',
    course_description: '',
    eligibility_criteria: ''
  });

  const [studentForm, setStudentForm] = useState({
    course_id: '',
    student_name: '',
    roll_number: '',
    email: '',
    mobile: '',
    gender: '',
    date_of_birth: '',
    father_name: '',
    mother_name: '',
    address: '',
    admission_date: '',
    expected_completion_date: ''
  });

  const [visitForm, setVisitForm] = useState({
    visit_date: '',
    visit_time: '',
    company_name: '',
    duration_hours: 2,
    visit_type: 'placement_drive',
    purpose: '',
    expected_students: ''
  });

  const [placementForm, setPlacementForm] = useState({
    course_id: '',
    batch_name: '',
    batch_year: new Date().getFullYear(),
    total_students: '',
    placed_students: '',
    average_salary: '',
    highest_salary: '',
    lowest_salary: '',
    top_companies: '',
    completion_date: ''
  });

  // Responsive breakpoints
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchInstitutionProfile();
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'courses') fetchCourses();
    if (activeTab === 'students') fetchStudents();
    if (activeTab === 'visits') fetchVisits();
    if (activeTab === 'placement') fetchPlacementBatches();
  }, [activeTab]);


  const fetchInstitutionProfile = async () => {
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setInstitution(data.institution);
      }
    } catch (error) {
      console.error('Error fetching institution profile:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCourses(data.courses);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async (page = studentsPage, search = studentsSearch) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: studentsLimit.toString(),
        ...(search && { search })
      });
      
      const response = await fetch(`${API_URL}/api/institution/students?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setStudents(data.students);
        setStudentsTotal(data.total);
        setStudentsPage(page);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/visits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setVisits(data.visits);
      }
    } catch (error) {
      console.error('Error fetching visits:', error);
      toast.error('Failed to fetch visits');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlacementBatches = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/placement-batches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPlacementBatches(data.batches);
      }
    } catch (error) {
      console.error('Error fetching placement batches:', error);
      toast.error('Failed to fetch placement batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/candidates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCandidates(data.candidates);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/students/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      setUploadResult(data);
      
      if (data.success) {
        toast.success(data.message);
        setShowUploadModal(false);
        setSelectedFile(null);
        fetchStudents(); // Refresh the students list
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem('gogig_institution_access');
    const link = document.createElement('a');
    link.href = `${API_URL}/api/institution/students/template`;
    link.download = 'students_template.csv';
    link.style.display = 'none';
    
    // Add authorization header
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API_URL}/api/institution/students/template`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'blob';
    
    xhr.onload = function() {
      if (xhr.status === 200) {
        const blob = new Blob([xhr.response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download template');
      }
    };
    
    xhr.send();
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('gogig_institution_access');
      await fetch(`${API_URL}/api/institution/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      if (onLogout) onLogout();
    }
  };

  // Course Management Functions
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/courses`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(courseForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Course created successfully!');
        setShowCourseModal(false);
        setCourseForm({
          course_name: '',
          trade_code: '',
          duration_months: '',
          total_seats: '',
          course_description: '',
          eligibility_criteria: ''
        });
        fetchCourses();
      } else {
        toast.error(data.message || 'Failed to create course');
      }
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error('Failed to create course');
    }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/courses/${editingItem.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(courseForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Course updated successfully!');
        setShowCourseModal(false);
        setEditingItem(null);
        setCourseForm({
          course_name: '',
          trade_code: '',
          duration_months: '',
          total_seats: '',
          course_description: '',
          eligibility_criteria: ''
        });
        fetchCourses();
      } else {
        toast.error(data.message || 'Failed to update course');
      }
    } catch (error) {
      console.error('Error updating course:', error);
      toast.error('Failed to update course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/courses/${courseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Course deleted successfully!');
        fetchCourses();
      } else {
        toast.error(data.message || 'Failed to delete course');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const handleEditCourse = (course) => {
    setEditingItem(course);
    setCourseForm({
      course_name: course.course_name,
      trade_code: course.trade_code || '',
      duration_months: course.duration_months || '',
      total_seats: course.total_seats,
      course_description: course.course_description || '',
      eligibility_criteria: course.eligibility_criteria || ''
    });
    setShowCourseModal(true);
  };

  // Student Management Functions
  const handleCreateStudent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/students`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(studentForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Student added successfully!');
        setShowStudentModal(false);
        setStudentForm({
          course_id: '',
          student_name: '',
          roll_number: '',
          email: '',
          mobile: '',
          gender: '',
          date_of_birth: '',
          father_name: '',
          mother_name: '',
          address: '',
          admission_date: '',
          expected_completion_date: ''
        });
        fetchStudents();
      } else {
        toast.error(data.message || 'Failed to add student');
      }
    } catch (error) {
      console.error('Error adding student:', error);
      toast.error('Failed to add student');
    }
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/students/${editingItem.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(studentForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Student updated successfully!');
        setShowStudentModal(false);
        setEditingItem(null);
        setStudentForm({
          course_id: '',
          student_name: '',
          roll_number: '',
          email: '',
          mobile: '',
          gender: '',
          date_of_birth: '',
          father_name: '',
          mother_name: '',
          address: '',
          admission_date: '',
          expected_completion_date: ''
        });
        fetchStudents();
      } else {
        toast.error(data.message || 'Failed to update student');
      }
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error('Failed to update student');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/students/${studentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Student deleted successfully!');
        fetchStudents();
      } else {
        toast.error(data.message || 'Failed to delete student');
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete student');
    }
  };

  const handleEditStudent = (student) => {
    setEditingItem(student);
    setStudentForm({
      course_id: student.course_id || '',
      student_name: student.student_name,
      roll_number: student.roll_number,
      email: student.email || '',
      mobile: student.mobile,
      gender: student.gender,
      date_of_birth: student.date_of_birth || '',
      father_name: student.father_name || '',
      mother_name: student.mother_name || '',
      address: student.address || '',
      admission_date: student.admission_date,
      expected_completion_date: student.expected_completion_date || ''
    });
    setShowStudentModal(true);
  };

  // Campus Visit Management Functions
  const handleCreateVisit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/visits`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(visitForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Campus visit scheduled successfully!');
        setShowVisitModal(false);
        setVisitForm({
          visit_date: '',
          visit_time: '',
          company_name: '',
          duration_hours: 2,
          visit_type: 'placement_drive',
          purpose: '',
          expected_students: ''
        });
        fetchVisits();
      } else {
        toast.error(data.message || 'Failed to schedule campus visit');
      }
    } catch (error) {
      console.error('Error scheduling campus visit:', error);
      toast.error('Failed to schedule campus visit');
    }
  };

  const handleUpdateVisit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/visits/${editingItem.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(visitForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Campus visit updated successfully!');
        setShowVisitModal(false);
        setEditingItem(null);
        setVisitForm({
          visit_date: '',
          visit_time: '',
          duration_hours: 2,
          visit_type: 'placement_drive',
          purpose: '',
          expected_students: ''
        });
        fetchVisits();
      } else {
        toast.error(data.message || 'Failed to update campus visit');
      }
    } catch (error) {
      console.error('Error updating campus visit:', error);
      toast.error('Failed to update campus visit');
    }
  };

  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm('Are you sure you want to delete this campus visit?')) return;
    
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/visits/${visitId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Campus visit deleted successfully!');
        fetchVisits();
      } else {
        toast.error(data.message || 'Failed to delete campus visit');
      }
    } catch (error) {
      console.error('Error deleting campus visit:', error);
      toast.error('Failed to delete campus visit');
    }
  };

  const handleEditVisit = (visit) => {
    setEditingItem(visit);
    setVisitForm({
      visit_date: visit.visit_date,
      visit_time: visit.visit_time || '',
      company_name: visit.company_name || '',
      duration_hours: visit.duration_hours || 2,
      visit_type: visit.visit_type || 'placement_drive',
      purpose: visit.purpose || '',
      expected_students: visit.expected_students || ''
    });
    setShowVisitModal(true);
  };

  // Placement Batch Management Functions
  const handleCreatePlacementBatch = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/placement-batches`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(placementForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Placement batch created successfully!');
        setShowPlacementModal(false);
        setPlacementForm({
          course_id: '',
          batch_name: '',
          batch_year: new Date().getFullYear(),
          total_students: '',
          placed_students: '',
          average_salary: '',
          highest_salary: '',
          lowest_salary: '',
          top_companies: '',
          completion_date: ''
        });
        fetchPlacementBatches();
      } else {
        toast.error(data.message || 'Failed to create placement batch');
      }
    } catch (error) {
      console.error('Error creating placement batch:', error);
      toast.error('Failed to create placement batch');
    }
  };

  const handleUpdatePlacementBatch = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/placement-batches/${editingItem.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(placementForm)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Placement batch updated successfully!');
        setShowPlacementModal(false);
        setEditingItem(null);
        setPlacementForm({
          course_id: '',
          batch_name: '',
          batch_year: new Date().getFullYear(),
          total_students: '',
          placed_students: '',
          average_salary: '',
          highest_salary: '',
          lowest_salary: '',
          top_companies: '',
          completion_date: ''
        });
        fetchPlacementBatches();
      } else {
        toast.error(data.message || 'Failed to update placement batch');
      }
    } catch (error) {
      console.error('Error updating placement batch:', error);
      toast.error('Failed to update placement batch');
    }
  };

  const handleDeletePlacementBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this placement batch?')) return;
    
    try {
      const token = localStorage.getItem('gogig_institution_access');
      const response = await fetch(`${API_URL}/api/institution/placement-batches/${batchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Placement batch deleted successfully!');
        fetchPlacementBatches();
      } else {
        toast.error(data.message || 'Failed to delete placement batch');
      }
    } catch (error) {
      console.error('Error deleting placement batch:', error);
      toast.error('Failed to delete placement batch');
    }
  };

  const handleEditPlacementBatch = (batch) => {
    setEditingItem(batch);
    setPlacementForm({
      course_id: batch.course_id || '',
      batch_name: batch.batch_name,
      batch_year: batch.batch_year,
      total_students: batch.total_students,
      placed_students: batch.placed_students || '',
      average_salary: batch.average_salary || '',
      highest_salary: batch.highest_salary || '',
      lowest_salary: batch.lowest_salary || '',
      top_companies: batch.top_companies || '',
      completion_date: batch.completion_date || ''
    });
    setShowPlacementModal(true);
  };

  const renderDashboard = () => (
    <div style={{ padding: isMobile ? 0 : '0 8px' }}>
      <h2 style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, color: '#0f172a', marginBottom: 20 }}>
        Dashboard Overview
      </h2>

      {/* Statistics Cards */}
      <div className="admin-stats-grid" style={{ marginBottom: 28 }}>
        <div className="admin-stat-card">
          <div className="admin-stat-card-icon primary">
            <FaGraduationCap style={{ color: '#fff', fontSize: 26 }} />
          </div>
          <div>
            <div className="admin-stat-card-label">Total Courses</div>
            <h3 className="admin-stat-card-value" style={{ margin: 0 }}>{statistics.totalCourses || 0}</h3>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card-icon amber">
            <FaUsers style={{ color: '#fff', fontSize: 26 }} />
          </div>
          <div>
            <div className="admin-stat-card-label">Total Students</div>
            <h3 className="admin-stat-card-value" style={{ margin: 0 }}>{statistics.totalStudents || 0}</h3>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card-icon primary">
            <FaUserGraduate style={{ color: '#fff', fontSize: 26 }} />
          </div>
          <div>
            <div className="admin-stat-card-label">Placed Students</div>
            <h3 className="admin-stat-card-value" style={{ margin: 0 }}>{statistics.placedStudents || 0}</h3>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card-icon amber">
            <FaChartBar style={{ color: '#fff', fontSize: 26 }} />
          </div>
          <div>
            <div className="admin-stat-card-label">Placement Rate</div>
            <h3 className="admin-stat-card-value" style={{ margin: 0 }}>{statistics.placementRate ? statistics.placementRate.toFixed(1) : 0}%</h3>
          </div>
        </div>
      </div>

      {/* Institution Info Card */}
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        marginBottom: 32
      }}>
        <h3 style={{ 
          fontSize: 20, 
          fontWeight: 700, 
          color: '#0d9488', 
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <FaUniversity />
          Institution Information
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
          gap: 16 
        }}>
          <div>
            <h4 style={{ fontSize: 16, fontWeight: 600, color: '#333', margin: '0 0 8px 0' }}>
              {institution?.name}
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaMapMarkerAlt style={{ color: '#666', fontSize: 14 }} />
              <span style={{ color: '#666', fontSize: 14 }}>
                {institution?.address}, {institution?.city}, {institution?.state} - {institution?.pincode}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaPhone style={{ color: '#666', fontSize: 14 }} />
              <span style={{ color: '#666', fontSize: 14 }}>{institution?.contact_mobile}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaEnvelope style={{ color: '#666', fontSize: 14 }} />
              <span style={{ color: '#666', fontSize: 14 }}>{institution?.email}</span>
            </div>
            {institution?.website && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaGlobe style={{ color: '#666', fontSize: 14 }} />
                <span style={{ color: '#666', fontSize: 14 }}>{institution?.website}</span>
              </div>
            )}
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaUsers style={{ color: '#666', fontSize: 14 }} />
              <span style={{ color: '#666', fontSize: 14 }}>
                Contact Person: {institution?.contact_person}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaStar style={{ color: '#fbbf24', fontSize: 14 }} />
              <span style={{ color: '#666', fontSize: 14 }}>
                Rating: {institution?.rating || 'Not rated yet'}
              </span>
            </div>
            <div style={{ 
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              background: institution?.verification_status === 'verified' ? '#dcfce7' : '#fef3c7',
              color: institution?.verification_status === 'verified' ? '#166534' : '#92400e'
            }}>
              {institution?.verification_status?.toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Visits */}
      {statistics.recentVisits && statistics.recentVisits.length > 0 && (
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ 
            fontSize: 20, 
            fontWeight: 700, 
            color: '#0d9488', 
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <FaCalendarAlt />
            Recent Campus Visits
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {statistics.recentVisits.map((visit, index) => (
              <div key={index} style={{
                padding: 16,
                background: '#f8fafc',
                borderRadius: 12,
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px 0' }}>
                      {visit.company_name || visit.visit_type.replace('_', ' ').toUpperCase()}
                    </h4>
                    <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
                      {new Date(visit.visit_date).toLocaleDateString()} at {visit.visit_time}
                    </p>
                  </div>
                  <div style={{
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    background: visit.status === 'completed' ? '#dcfce7' : '#fef3c7',
                    color: visit.status === 'completed' ? '#166534' : '#92400e'
                  }}>
                    {visit.status.toUpperCase()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderCoursesManagement = () => (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 0
      }}>
        <div>
          <h2 style={{ 
            fontSize: isMobile ? 20 : 24, 
            fontWeight: 700, 
            color: '#0d9488', 
            margin: 0 
          }}>
            Course Management
          </h2>
          <p style={{ 
            fontSize: 14, 
            color: '#666', 
            margin: '4px 0 0 0' 
          }}>
            Manage your institution's courses and trades
          </p>
        </div>
        <button
          onClick={() => {
            setEditingItem(null);
            setCourseForm({
              course_name: '',
              trade_code: '',
              duration_months: '',
              total_seats: '',
              course_description: '',
              eligibility_criteria: ''
            });
            setShowCourseModal(true);
          }}
          style={{
            background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '12px 24px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 16px rgba(102,126,234,0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
        >
          <FaPlus />
          Add Course
        </button>
      </div>

      {/* Courses Table */}
      <div style={{ 
        background: '#fff', 
        borderRadius: 16, 
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {courses.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Course Name</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Trade Code</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Duration</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Total Seats</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Current Students</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Placed Students</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Placement %</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Status</th>
                  <th style={{ padding: 16, textAlign: 'center', fontWeight: 700, color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course, idx) => (
                  <tr key={course.id} style={{ 
                    background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <td style={{ padding: 16, fontWeight: 600, color: '#374151' }}>{course.course_name}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{course.trade_code || '-'}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>
                      {course.duration_months ? `${course.duration_months} months` : '-'}
                    </td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{course.total_seats}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{course.current_students || 0}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>
                      <span style={{
                        background: '#dcfce7',
                        color: '#166534',
                        padding: '4px 8px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {course.placed_students || 0}
                      </span>
                    </td>
                    <td style={{ padding: 16, color: '#6b7280' }}>
                      <span style={{
                        background: course.placement_percentage >= 70 ? '#dcfce7' : course.placement_percentage >= 50 ? '#fef3c7' : '#fee2e2',
                        color: course.placement_percentage >= 70 ? '#166534' : course.placement_percentage >= 50 ? '#92400e' : '#dc2626',
                        padding: '4px 8px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {course.placement_percentage ? `${course.placement_percentage}%` : '0%'}
                      </span>
                    </td>
                    <td style={{ padding: 16 }}>
                      <span style={{
                        background: course.status === 'active' ? '#dcfce7' : '#fef3c7',
                        color: course.status === 'active' ? '#166534' : '#92400e',
                        padding: '4px 12px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {course.status}
                      </span>
                    </td>
                    <td style={{ padding: 16, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEditCourse(course)}
                          style={{
                            background: '#0d9488',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#5a67d8'}
                          onMouseOut={(e) => e.target.style.background = '#0d9488'}
                        >
                          <FaEdit />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCourse(course.id)}
                          style={{
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#dc2626'}
                          onMouseOut={(e) => e.target.style.background = '#ef4444'}
                        >
                          <FaTrash />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            padding: 60, 
            textAlign: 'center', 
            color: '#6b7280' 
          }}>
            <FaGraduationCap style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px 0' }}>
              No courses found
            </h3>
            <p style={{ fontSize: 14, margin: 0 }}>
              Click "Add Course" to create your first course
            </p>
          </div>
        )}
      </div>

      {/* Course Modal */}
      {showCourseModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }} onClick={() => setShowCourseModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 32,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: '#0d9488', 
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <FaGraduationCap />
              {editingItem ? 'Edit Course' : 'Create New Course'}
            </h3>
            
            <form onSubmit={editingItem ? handleUpdateCourse : handleCreateCourse} style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 20 
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Course Name *
                  </label>
                  <input
                    type="text"
                    value={courseForm.course_name}
                    onChange={e => setCourseForm({ ...courseForm, course_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Trade Code
                  </label>
                  <input
                    type="text"
                    value={courseForm.trade_code}
                    onChange={e => setCourseForm({ ...courseForm, trade_code: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Duration (months)
                  </label>
                  <input
                    type="number"
                    value={courseForm.duration_months}
                    onChange={e => setCourseForm({ ...courseForm, duration_months: parseInt(e.target.value) || '' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Total Seats *
                  </label>
                  <input
                    type="number"
                    value={courseForm.total_seats}
                    onChange={e => setCourseForm({ ...courseForm, total_seats: parseInt(e.target.value) || '' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  />
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Course Description
                </label>
                <textarea
                  value={courseForm.course_description}
                  onChange={e => setCourseForm({ ...courseForm, course_description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: 80
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  rows={3}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Eligibility Criteria
                </label>
                <textarea
                  value={courseForm.eligibility_criteria}
                  onChange={e => setCourseForm({ ...courseForm, eligibility_criteria: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: 80
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  rows={3}
                />
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: 12, 
                marginTop: 8,
                justifyContent: 'flex-end'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowCourseModal(false)}
                  style={{
                    padding: '12px 24px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#6b7280',
                    background: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 16px rgba(102,126,234,0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {editingItem ? 'Update Course' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderStudentsManagement = () => (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 0
      }}>
        <div>
          <h2 style={{ 
            fontSize: isMobile ? 20 : 24, 
            fontWeight: 700, 
            color: '#0d9488', 
            margin: 0 
          }}>
            Student Management
          </h2>
          <p style={{ 
            fontSize: 14, 
            color: '#666', 
            margin: '4px 0 0 0' 
          }}>
            Manage your institution's students
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
          <button
            onClick={() => {
              setSelectedFile(null);
              setUploadResult(null);
              setShowUploadModal(true);
            }}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 16px rgba(16,185,129,0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <FaUpload />
            Bulk Upload
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              setStudentForm({
                course_id: '',
                student_name: '',
                roll_number: '',
                email: '',
                mobile: '',
                gender: '',
                date_of_birth: '',
                father_name: '',
                mother_name: '',
                address: '',
                admission_date: '',
                expected_completion_date: ''
              });
              setShowStudentModal(true);
            }}
            style={{
              background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 16px rgba(102,126,234,0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <FaPlus />
            Add Student
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ 
        background: '#fff', 
        borderRadius: 12, 
        padding: 16, 
        marginBottom: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <FaSearch style={{ 
              position: 'absolute', 
              left: 12, 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#9ca3af' 
            }} />
            <input
              type="text"
              placeholder="Search students by name, roll number, email, or mobile..."
              value={studentsSearch}
              onChange={(e) => {
                setStudentsSearch(e.target.value);
                if (e.target.value === '') {
                  fetchStudents(1, '');
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  fetchStudents(1, studentsSearch);
                }
              }}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                border: '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0d9488'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
          <button
            onClick={() => fetchStudents(1, studentsSearch)}
            style={{
              background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px 20px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaSearch />
            Search
          </button>
        </div>
      </div>

      {/* Students Table */}
      <div style={{ 
        background: '#fff', 
        borderRadius: 16, 
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {students.length > 0 ? (
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 800 : 1000 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Student Name</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Roll Number</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Course</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Email</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Mobile</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Gender</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Placement Status</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Company</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Status</th>
                  <th style={{ padding: 16, textAlign: 'center', fontWeight: 700, color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, idx) => (
                  <tr key={student.id} style={{ 
                    background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <td style={{ padding: 16, fontWeight: 600, color: '#374151' }}>{student.student_name}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{student.roll_number}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>
                      {student.InstitutionCourse?.course_name || '-'}
                    </td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{student.email || '-'}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{student.mobile}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{student.gender}</td>
                    <td style={{ padding: 16 }}>
                      <span style={{
                        background: student.placement_status === 'placed' ? '#dcfce7' : 
                                   student.placement_status === 'under_process' ? '#fef3c7' : '#fee2e2',
                        color: student.placement_status === 'placed' ? '#166534' : 
                               student.placement_status === 'under_process' ? '#92400e' : '#dc2626',
                        padding: '4px 12px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {student.placement_status || 'not_placed'}
                      </span>
                    </td>
                    <td style={{ padding: 16, color: '#6b7280' }}>
                      {student.placement_status === 'placed' && student.company_name ? (
                        <span style={{
                          background: '#e0f2fe',
                          color: '#0369a1',
                          padding: '4px 8px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600
                        }}>
                          {student.company_name}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={{ padding: 16 }}>
                      <span style={{
                        background: student.status === 'active' ? '#dcfce7' : '#fef3c7',
                        color: student.status === 'active' ? '#166534' : '#92400e',
                        padding: '4px 12px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {student.status}
                      </span>
                    </td>
                    <td style={{ padding: 16, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEditStudent(student)}
                          style={{
                            background: '#0d9488',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#5a67d8'}
                          onMouseOut={(e) => e.target.style.background = '#0d9488'}
                        >
                          <FaEdit />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteStudent(student.id)}
                          style={{
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#dc2626'}
                          onMouseOut={(e) => e.target.style.background = '#ef4444'}
                        >
                          <FaTrash />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            padding: 60, 
            textAlign: 'center', 
            color: '#6b7280' 
          }}>
            <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px 0' }}>
              No students found
            </h3>
            <p style={{ fontSize: 14, margin: 0 }}>
              Click "Add Student" to add your first student
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {studentsTotal > studentsLimit && (
        <div style={{ 
          background: '#fff', 
          borderRadius: 12, 
          padding: 16, 
          marginTop: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div style={{ 
            fontSize: 14, 
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <span>Showing</span>
            <span style={{ fontWeight: 600, color: '#374151' }}>
              {((studentsPage - 1) * studentsLimit) + 1}
            </span>
            <span>to</span>
            <span style={{ fontWeight: 600, color: '#374151' }}>
              {Math.min(studentsPage * studentsLimit, studentsTotal)}
            </span>
            <span>of</span>
            <span style={{ fontWeight: 600, color: '#374151' }}>
              {studentsTotal}
            </span>
            <span>students</span>
          </div>
          
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => fetchStudents(studentsPage - 1, studentsSearch)}
              disabled={studentsPage === 1}
              style={{
                background: studentsPage === 1 ? '#f3f4f6' : '#fff',
                color: studentsPage === 1 ? '#9ca3af' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 14,
                fontWeight: 600,
                cursor: studentsPage === 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s ease'
              }}
            >
              Previous
            </button>
            
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: Math.ceil(studentsTotal / studentsLimit) }, (_, i) => i + 1)
                .filter(page => {
                  const totalPages = Math.ceil(studentsTotal / studentsLimit);
                  return page === 1 || page === totalPages || 
                         (page >= studentsPage - 1 && page <= studentsPage + 1);
                })
                .map((page, index, array) => {
                  const showEllipsis = index > 0 && page - array[index - 1] > 1;
                  return (
                    <React.Fragment key={page}>
                      {showEllipsis && (
                        <span style={{ 
                          padding: '8px 4px', 
                          color: '#9ca3af',
                          fontSize: 14
                        }}>
                          ...
                        </span>
                      )}
                      <button
                        onClick={() => fetchStudents(page, studentsSearch)}
                        style={{
                          background: page === studentsPage ? '#0d9488' : '#fff',
                          color: page === studentsPage ? '#fff' : '#374151',
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          minWidth: 40,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          if (page !== studentsPage) {
                            e.target.style.background = '#f3f4f6';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (page !== studentsPage) {
                            e.target.style.background = '#fff';
                          }
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
            </div>
            
            <button
              onClick={() => fetchStudents(studentsPage + 1, studentsSearch)}
              disabled={studentsPage >= Math.ceil(studentsTotal / studentsLimit)}
              style={{
                background: studentsPage >= Math.ceil(studentsTotal / studentsLimit) ? '#f3f4f6' : '#fff',
                color: studentsPage >= Math.ceil(studentsTotal / studentsLimit) ? '#9ca3af' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 14,
                fontWeight: 600,
                cursor: studentsPage >= Math.ceil(studentsTotal / studentsLimit) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s ease'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Student Modal */}
      {showStudentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }} onClick={() => setShowStudentModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 32,
            maxWidth: 700,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: '#0d9488', 
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <FaUsers />
              {editingItem ? 'Edit Student' : 'Add New Student'}
            </h3>
            
            <form onSubmit={editingItem ? handleUpdateStudent : handleCreateStudent} style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 20 
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Course *
                </label>
                <select
                  value={studentForm.course_id}
                  onChange={e => setStudentForm({ ...studentForm, course_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                    background: '#fff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  required
                >
                  <option value="">Select Course *</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.course_name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Student Name *
                  </label>
                  <input
                    type="text"
                    value={studentForm.student_name}
                    onChange={e => setStudentForm({ ...studentForm, student_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Roll Number *
                  </label>
                  <input
                    type="text"
                    value={studentForm.roll_number}
                    onChange={e => setStudentForm({ ...studentForm, roll_number: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={studentForm.email}
                    onChange={e => setStudentForm({ ...studentForm, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Mobile *
                  </label>
                  <input
                    type="tel"
                    value={studentForm.mobile}
                    onChange={e => setStudentForm({ ...studentForm, mobile: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Gender *
                  </label>
                  <select
                    value={studentForm.gender}
                    onChange={e => setStudentForm({ ...studentForm, gender: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none',
                      background: '#fff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  >
                    <option value="">Select Gender *</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={studentForm.date_of_birth}
                    onChange={e => setStudentForm({ ...studentForm, date_of_birth: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Father Name
                  </label>
                  <input
                    type="text"
                    value={studentForm.father_name}
                    onChange={e => setStudentForm({ ...studentForm, father_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Mother Name
                  </label>
                  <input
                    type="text"
                    value={studentForm.mother_name}
                    onChange={e => setStudentForm({ ...studentForm, mother_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Address
                </label>
                <textarea
                  value={studentForm.address}
                  onChange={e => setStudentForm({ ...studentForm, address: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: 60
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  rows={2}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Admission Date *
                  </label>
                  <input
                    type="date"
                    value={studentForm.admission_date}
                    onChange={e => setStudentForm({ ...studentForm, admission_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Expected Completion Date
                  </label>
                  <input
                    type="date"
                    value={studentForm.expected_completion_date}
                    onChange={e => setStudentForm({ ...studentForm, expected_completion_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: 12, 
                marginTop: 8,
                justifyContent: 'flex-end'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowStudentModal(false)}
                  style={{
                    padding: '12px 24px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#6b7280',
                    background: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 16px rgba(102,126,234,0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {editingItem ? 'Update Student' : 'Add Student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderCampusVisitsManagement = () => (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 0
      }}>
        <div>
          <h2 style={{ 
            fontSize: isMobile ? 20 : 24, 
            fontWeight: 700, 
            color: '#0d9488', 
            margin: 0 
          }}>
            Campus Visits Management
          </h2>
          <p style={{ 
            fontSize: 14, 
            color: '#666', 
            margin: '4px 0 0 0' 
          }}>
            Schedule and track campus visits
          </p>
        </div>
        <button
          onClick={() => {
            setEditingItem(null);
            setVisitForm({
              visit_date: '',
              visit_time: '',
              company_name: '',
              duration_hours: 2,
              visit_type: 'placement_drive',
              purpose: '',
              expected_students: ''
            });
            setShowVisitModal(true);
          }}
          style={{
            background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '12px 24px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 16px rgba(102,126,234,0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
        >
          <FaPlus />
          Schedule Visit
        </button>
      </div>

      {/* Campus Visits Table */}
      <div style={{ 
        background: '#fff', 
        borderRadius: 16, 
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {visits.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Visit Date</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Time</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Company</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Type</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Purpose</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Expected Students</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Status</th>
                  <th style={{ padding: 16, textAlign: 'center', fontWeight: 700, color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit, idx) => (
                  <tr key={visit.id} style={{ 
                    background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <td style={{ padding: 16, fontWeight: 600, color: '#374151' }}>
                      {new Date(visit.visit_date).toLocaleDateString()}
                    </td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{visit.visit_time || '-'}</td>
                    <td style={{ padding: 16, color: '#0d9488', fontWeight: 600 }}>
                      {visit.company_name || '-'}
                    </td>
                    <td style={{ padding: 16, color: '#6b7280' }}>
                      {visit.visit_type?.replace('_', ' ').toUpperCase() || '-'}
                    </td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{visit.purpose || '-'}</td>
                    <td style={{ padding: 16, color: '#6b7280' }}>{visit.expected_students || '-'}</td>
                    <td style={{ padding: 16 }}>
                      <span style={{
                        background: visit.status === 'completed' ? '#dcfce7' : 
                                   visit.status === 'scheduled' ? '#dbeafe' : 
                                   visit.status === 'in_progress' ? '#fef3c7' : '#fee2e2',
                        color: visit.status === 'completed' ? '#166534' : 
                               visit.status === 'scheduled' ? '#1e40af' : 
                               visit.status === 'in_progress' ? '#92400e' : '#991b1b',
                        padding: '4px 12px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {visit.status}
                      </span>
                    </td>
                    <td style={{ padding: 16, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEditVisit(visit)}
                          style={{
                            background: '#0d9488',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#5a67d8'}
                          onMouseOut={(e) => e.target.style.background = '#0d9488'}
                        >
                          <FaEdit />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteVisit(visit.id)}
                          style={{
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#dc2626'}
                          onMouseOut={(e) => e.target.style.background = '#ef4444'}
                        >
                          <FaTrash />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            padding: 60, 
            textAlign: 'center', 
            color: '#6b7280' 
          }}>
            <FaCalendarAlt style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px 0' }}>
              No campus visits found
            </h3>
            <p style={{ fontSize: 14, margin: 0 }}>
              Click "Schedule Visit" to schedule your first campus visit
            </p>
          </div>
        )}
      </div>

      {/* Campus Visit Modal */}
      {showVisitModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }} onClick={() => setShowVisitModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 32,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: '#0d9488', 
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <FaCalendarAlt />
              {editingItem ? 'Edit Campus Visit' : 'Schedule Campus Visit'}
            </h3>
            
            <form onSubmit={editingItem ? handleUpdateVisit : handleCreateVisit} style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 20 
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Visit Date *
                  </label>
                  <input
                    type="date"
                    value={visitForm.visit_date}
                    onChange={e => setVisitForm({ ...visitForm, visit_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Visit Time
                  </label>
                  <input
                    type="time"
                    value={visitForm.visit_time}
                    onChange={e => setVisitForm({ ...visitForm, visit_time: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Company Name *
                </label>
                <input
                  type="text"
                  value={visitForm.company_name}
                  onChange={e => setVisitForm({ ...visitForm, company_name: e.target.value })}
                  placeholder="Enter company name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  required
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Visit Type *
                  </label>
                  <select
                    value={visitForm.visit_type}
                    onChange={e => setVisitForm({ ...visitForm, visit_type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none',
                      background: '#fff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  >
                    <option value="placement_drive">Placement Drive</option>
                    <option value="company_visit">Company Visit</option>
                    <option value="industry_talk">Industry Talk</option>
                    <option value="workshop">Workshop</option>
                    <option value="seminar">Seminar</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Duration (hours)
                  </label>
                  <input
                    type="number"
                    value={visitForm.duration_hours}
                    onChange={e => setVisitForm({ ...visitForm, duration_hours: parseInt(e.target.value) || 2 })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    min="1"
                    max="8"
                  />
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Purpose *
                </label>
                <input
                  type="text"
                  value={visitForm.purpose}
                  onChange={e => setVisitForm({ ...visitForm, purpose: e.target.value })}
                  placeholder="e.g., Recruitment drive for IT companies"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  required
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Expected Students
                </label>
                <input
                  type="number"
                  value={visitForm.expected_students}
                  onChange={e => setVisitForm({ ...visitForm, expected_students: parseInt(e.target.value) || '' })}
                  placeholder="Number of students expected to attend"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  min="1"
                />
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: 12, 
                marginTop: 8,
                justifyContent: 'flex-end'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowVisitModal(false)}
                  style={{
                    padding: '12px 24px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#6b7280',
                    background: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 16px rgba(102,126,234,0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {editingItem ? 'Update Visit' : 'Schedule Visit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderPlacementBatchesManagement = () => (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 0
      }}>
        <div>
          <h2 style={{ 
            fontSize: isMobile ? 20 : 24, 
            fontWeight: 700, 
            color: '#0d9488', 
            margin: 0 
          }}>
            Placement Batches Management
          </h2>
          <p style={{ 
            fontSize: 14, 
            color: '#666', 
            margin: '4px 0 0 0' 
          }}>
            Track placement performance and statistics
          </p>
        </div>
        <button
          onClick={() => {
            setEditingItem(null);
            setPlacementForm({
              course_id: '',
              batch_name: '',
              batch_year: new Date().getFullYear(),
              total_students: '',
              placed_students: '',
              average_salary: '',
              highest_salary: '',
              lowest_salary: '',
              top_companies: '',
              completion_date: ''
            });
            setShowPlacementModal(true);
          }}
          style={{
            background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '12px 24px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 16px rgba(102,126,234,0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
        >
          <FaPlus />
          Add Batch
        </button>
      </div>

      {/* Placement Batches Table */}
      <div style={{ 
        background: '#fff', 
        borderRadius: 16, 
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {placementBatches.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Batch Name</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Course</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Year</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Total Students</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Placed</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Avg Salary</th>
                  <th style={{ padding: 16, textAlign: 'left', fontWeight: 700, color: '#374151' }}>Placement Rate</th>
                  <th style={{ padding: 16, textAlign: 'center', fontWeight: 700, color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {placementBatches.map((batch, idx) => {
                  const placementRate = batch.total_students > 0 ? 
                    ((batch.placed_students || 0) / batch.total_students * 100).toFixed(1) : 0;
                  
                  return (
                    <tr key={batch.id} style={{ 
                      background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      <td style={{ padding: 16, fontWeight: 600, color: '#374151' }}>{batch.batch_name}</td>
                      <td style={{ padding: 16, color: '#6b7280' }}>
                        {batch.InstitutionCourse?.course_name || '-'}
                      </td>
                      <td style={{ padding: 16, color: '#6b7280' }}>{batch.batch_year}</td>
                      <td style={{ padding: 16, color: '#6b7280' }}>{batch.total_students}</td>
                      <td style={{ padding: 16, color: '#6b7280' }}>{batch.placed_students || 0}</td>
                      <td style={{ padding: 16, color: '#6b7280' }}>
                        {batch.average_salary ? `₹${batch.average_salary.toLocaleString()}` : '-'}
                      </td>
                      <td style={{ padding: 16 }}>
                        <span style={{
                          background: placementRate >= 80 ? '#dcfce7' : 
                                     placementRate >= 60 ? '#fef3c7' : '#fee2e2',
                          color: placementRate >= 80 ? '#166534' : 
                                 placementRate >= 60 ? '#92400e' : '#991b1b',
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600
                        }}>
                          {placementRate}%
                        </span>
                      </td>
                      <td style={{ padding: 16, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEditPlacementBatch(batch)}
                            style={{
                              background: '#0d9488',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              padding: '8px 12px',
                              fontSize: 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#5a67d8'}
                            onMouseOut={(e) => e.target.style.background = '#0d9488'}
                          >
                            <FaEdit />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeletePlacementBatch(batch.id)}
                            style={{
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              padding: '8px 12px',
                              fontSize: 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#dc2626'}
                            onMouseOut={(e) => e.target.style.background = '#ef4444'}
                          >
                            <FaTrash />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            padding: 60, 
            textAlign: 'center', 
            color: '#6b7280' 
          }}>
            <FaUserGraduate style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px 0' }}>
              No placement batches found
            </h3>
            <p style={{ fontSize: 14, margin: 0 }}>
              Click "Add Batch" to create your first placement batch
            </p>
          </div>
        )}
      </div>

      {/* Placement Batch Modal */}
      {showPlacementModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }} onClick={() => setShowPlacementModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 32,
            maxWidth: 700,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: '#0d9488', 
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <FaUserGraduate />
              {editingItem ? 'Edit Placement Batch' : 'Add Placement Batch'}
            </h3>
            
            <form onSubmit={editingItem ? handleUpdatePlacementBatch : handleCreatePlacementBatch} style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 20 
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Course *
                </label>
                <select
                  value={placementForm.course_id}
                  onChange={e => setPlacementForm({ ...placementForm, course_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                    background: '#fff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  required
                >
                  <option value="">Select Course *</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.course_name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Batch Name *
                  </label>
                  <input
                    type="text"
                    value={placementForm.batch_name}
                    onChange={e => setPlacementForm({ ...placementForm, batch_name: e.target.value })}
                    placeholder="e.g., 2024 IT Batch"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    required
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Batch Year *
                  </label>
                  <input
                    type="number"
                    value={placementForm.batch_year}
                    onChange={e => setPlacementForm({ ...placementForm, batch_year: parseInt(e.target.value) || new Date().getFullYear() })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    min="2020"
                    max="2030"
                    required
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Total Students *
                  </label>
                  <input
                    type="number"
                    value={placementForm.total_students}
                    onChange={e => setPlacementForm({ ...placementForm, total_students: parseInt(e.target.value) || '' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Placed Students
                  </label>
                  <input
                    type="number"
                    value={placementForm.placed_students}
                    onChange={e => setPlacementForm({ ...placementForm, placed_students: parseInt(e.target.value) || '' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    min="0"
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Average Salary (₹)
                  </label>
                  <input
                    type="number"
                    value={placementForm.average_salary}
                    onChange={e => setPlacementForm({ ...placementForm, average_salary: parseInt(e.target.value) || '' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    min="0"
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Highest Salary (₹)
                  </label>
                  <input
                    type="number"
                    value={placementForm.highest_salary}
                    onChange={e => setPlacementForm({ ...placementForm, highest_salary: parseInt(e.target.value) || '' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    min="0"
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#374151', 
                    marginBottom: 8 
                  }}>
                    Lowest Salary (₹)
                  </label>
                  <input
                    type="number"
                    value={placementForm.lowest_salary}
                    onChange={e => setPlacementForm({ ...placementForm, lowest_salary: parseInt(e.target.value) || '' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    min="0"
                  />
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Top Companies
                </label>
                <input
                  type="text"
                  value={placementForm.top_companies}
                  onChange={e => setPlacementForm({ ...placementForm, top_companies: e.target.value })}
                  placeholder="e.g., TCS, Infosys, Wipro, HCL"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  marginBottom: 8 
                }}>
                  Completion Date
                </label>
                <input
                  type="date"
                  value={placementForm.completion_date}
                  onChange={e => setPlacementForm({ ...placementForm, completion_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0d9488'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: 12, 
                marginTop: 8,
                justifyContent: 'flex-end'
              }}>
                <button 
                  type="button" 
                  onClick={() => setShowPlacementModal(false)}
                  style={{
                    padding: '12px 24px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#6b7280',
                    background: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 16px rgba(102,126,234,0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {editingItem ? 'Update Batch' : 'Add Batch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );

  const renderCandidatesManagement = () => (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 0
      }}>
        <div>
          <h2 style={{ 
            fontSize: isMobile ? 20 : 24, 
            fontWeight: 700, 
            color: '#0d9488', 
            margin: 0 
          }}>
            Candidate Management
          </h2>
          <p style={{ 
            fontSize: 14, 
            color: '#666', 
            margin: '4px 0 0 0' 
          }}>
            Upload and manage candidates via CSV
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
          <button
            onClick={downloadTemplate}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 20px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <FaDownload />
            Download Template
          </button>
          <button
            onClick={() => {
              setSelectedFile(null);
              setUploadResult(null);
              setShowUploadModal(true);
            }}
            style={{
              background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 20px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <FaUpload />
            Upload CSV
          </button>
        </div>
      </div>


      {/* Candidates Table */}
      <div style={{ 
        background: '#fff', 
        borderRadius: 16, 
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        marginBottom: 24
      }}>
        {candidates.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)' }}>
                  <th style={{ padding: 16, color: '#fff', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>
                    Name
                  </th>
                  <th style={{ padding: 16, color: '#fff', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>
                    Email
                  </th>
                  <th style={{ padding: 16, color: '#fff', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>
                    Mobile
                  </th>
                  <th style={{ padding: 16, color: '#fff', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>
                    Qualification
                  </th>
                  <th style={{ padding: 16, color: '#fff', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>
                    Course
                  </th>
                  <th style={{ padding: 16, color: '#fff', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>
                    Referral Code
                  </th>
                  <th style={{ padding: 16, color: '#fff', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>
                    Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate, index) => (
                  <tr key={candidate.id} style={{ 
                    borderBottom: index < candidates.length - 1 ? '1px solid #e5e7eb' : 'none',
                    background: index % 2 === 0 ? '#fff' : '#f9fafb'
                  }}>
                    <td style={{ padding: 16, fontSize: 14, color: '#374151' }}>
                      {candidate.name}
                    </td>
                    <td style={{ padding: 16, fontSize: 14, color: '#374151' }}>
                      {candidate.email}
                    </td>
                    <td style={{ padding: 16, fontSize: 14, color: '#374151' }}>
                      {candidate.mobile}
                    </td>
                    <td style={{ padding: 16, fontSize: 14, color: '#374151' }}>
                      {candidate.qualification || '-'}
                    </td>
                    <td style={{ padding: 16, fontSize: 14, color: '#374151' }}>
                      {candidate.course || '-'}
                    </td>
                    <td style={{ padding: 16, fontSize: 14, color: '#374151' }}>
                      {candidate.referral_code || '-'}
                    </td>
                    <td style={{ padding: 16, fontSize: 14, color: '#374151' }}>
                      {candidate.created_at ? new Date(candidate.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            padding: 40, 
            textAlign: 'center', 
            color: '#6b7280' 
          }}>
            <FaBuilding style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 600 }}>
              No candidates uploaded yet
            </h3>
            <p style={{ margin: 0, fontSize: 14 }}>
              Upload a CSV file to add candidates to the system
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="admin-layout" style={{ minHeight: '100vh', background: '#f1f5f9', position: 'relative' }}>
      <Toaster
        position={isMobile ? "top-center" : "top-right"}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#0f172a',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            padding: '16px 20px',
            fontSize: isMobile ? '14px' : '16px',
          },
        }}
      />

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: 20
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 24,
            width: '100%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20
            }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0d9488' }}>
                Upload Students CSV
              </h3>
              <button
                onClick={() => setShowUploadModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleCsvUpload}>
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 600,
                  color: '#374151'
                }}>
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '2px dashed #d1d5db',
                    borderRadius: 8,
                    fontSize: 14,
                    background: '#f9fafb'
                  }}
                />
                {selectedFile && (
                  <p style={{ margin: '8px 0 0 0', fontSize: 14, color: '#10b981' }}>
                    Selected: {selectedFile.name}
                  </p>
                )}
              </div>

              <div style={{
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: 8,
                padding: 12,
                marginBottom: 20
              }}>
                <p style={{ margin: 0, fontSize: 13, color: '#0369a1' }}>
                  <strong>CSV Format:</strong> Course Name, Student Name, Roll Number, Email, Mobile, Gender, Date of Birth, Father Name, Mother Name, Address, Admission Date, Expected Completion Date, Placement Status, Company Name
                </p>
              </div>

              {uploadResult && (
                <div style={{
                  background: uploadResult.success ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${uploadResult.success ? '#bbf7d0' : '#fecaca'}`,
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 20
                }}>
                  <p style={{ 
                    margin: 0, 
                    fontSize: 14, 
                    fontWeight: 600,
                    color: uploadResult.success ? '#059669' : '#dc2626'
                  }}>
                    {uploadResult.success ? '✅ Upload Successful!' : '❌ Upload Failed'}
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: 13 }}>
                    {uploadResult.message}
                  </p>
                  {uploadResult.success && uploadResult.stats && (
                    <div style={{ marginTop: 8 }}>
                      <p style={{ margin: '0 0 4px 0', fontSize: 12, fontWeight: 600 }}>
                        Statistics:
                      </p>
                      <p style={{ margin: '2px 0', fontSize: 12 }}>
                        • Total rows: {uploadResult.stats.total}
                      </p>
                      <p style={{ margin: '2px 0', fontSize: 12 }}>
                        • Imported: {uploadResult.stats.imported}
                      </p>
                      <p style={{ margin: '2px 0', fontSize: 12 }}>
                        • Errors: {uploadResult.stats.errors}
                      </p>
                    </div>
                  )}
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <p style={{ margin: '0 0 4px 0', fontSize: 12, fontWeight: 600 }}>
                        Errors:
                      </p>
                      {uploadResult.errors.slice(0, 5).map((error, index) => (
                        <p key={index} style={{ margin: '2px 0', fontSize: 12, color: '#dc2626' }}>
                          {error}
                        </p>
                      ))}
                      {uploadResult.errors.length > 5 && (
                        <p style={{ margin: '2px 0', fontSize: 12, color: '#dc2626' }}>
                          ... and {uploadResult.errors.length - 5} more errors
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 20px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <FaDownload />
                  Download Template
                </button>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    style={{
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: 8,
                      padding: '12px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedFile || uploading}
                    style={{
                      background: uploading ? '#9ca3af' : 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '12px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    {uploading ? (
                      <>
                        <div style={{
                          width: 16,
                          height: 16,
                          border: '2px solid #fff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaUpload />
                        Upload
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className="admin-sidebar"
        style={{
          width: isMobile ? (sidebarOpen ? 280 : 0) : (isTablet ? 240 : 280),
          position: isMobile ? 'fixed' : 'relative',
          left: isMobile ? (sidebarOpen ? 0 : -280) : 0,
          zIndex: 999,
          transition: 'left 0.3s ease, width 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {isMobile && (
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: '#f8fafc',
              fontSize: 18,
              cursor: 'pointer',
              zIndex: 10,
              width: 36,
              height: 36,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Close menu"
          >
            <FaTimes />
          </button>
        )}

        <div className="admin-sidebar-brand">
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(13, 148, 136, 0.4)',
          }}>
            <FaUniversity style={{ color: '#fff', fontSize: 26 }} />
          </div>
          <span className="admin-sidebar-brand-title" style={{ fontSize: '1rem' }}>
            {institution?.name || 'Institution'}
          </span>
          <span className="admin-sidebar-brand-badge">Dashboard</span>
        </div>

        <nav className="admin-sidebar-nav">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: FaChartBar },
            { id: 'courses', label: 'Courses', icon: FaGraduationCap },
            { id: 'students', label: 'Students', icon: FaUsers },
            { id: 'visits', label: 'Campus Visits', icon: FaCalendarAlt },
            { id: 'placement', label: 'Placement Batches', icon: FaUserGraduate }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`admin-sidebar-nav-btn ${activeTab === id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(id);
                if (isMobile) setSidebarOpen(false);
              }}
            >
              <span className="nav-icon"><Icon /></span>
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <button type="button" className="admin-sidebar-logout" onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <FaSignOutAlt style={{ fontSize: 18 }} />
          Logout
        </button>
      </aside>

      {/* Main Content */}
      <main
        className="admin-main"
        style={{
          padding: isMobile ? 16 : (isTablet ? 24 : 28),
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          marginBottom: isMobile ? 24 : 32,
          gap: isMobile ? 16 : 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0d9488',
                  fontSize: 20,
                  cursor: 'pointer',
                  padding: 8,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaBars />
              </button>
            )}
            <div style={{ flex: 1 }}>
              <h1 style={{ 
                fontSize: isMobile ? 24 : (isTablet ? 28 : 32), 
                fontWeight: 800, 
                color: '#0d9488', 
                margin: 0, 
                marginBottom: isMobile ? 4 : 8 
              }}>
                {activeTab === 'dashboard' && 'Dashboard'}
                {activeTab === 'courses' && 'Course Management'}
                {activeTab === 'students' && 'Student Management'}
                {activeTab === 'visits' && 'Campus Visits'}
                {activeTab === 'placement' && 'Placement Batches'}
                {activeTab === 'candidates' && 'Candidate Management'}
              </h1>
              <p style={{ 
                fontSize: isMobile ? 14 : 16, 
                color: '#666', 
                margin: 0,
                display: isMobile ? 'none' : 'block'
              }}>
                {activeTab === 'dashboard' && 'Overview of your institution'}
                {activeTab === 'courses' && 'Manage courses and trades'}
                {activeTab === 'students' && 'Manage student records'}
                {activeTab === 'visits' && 'Schedule and track campus visits'}
                {activeTab === 'placement' && 'Track placement performance'}
                {activeTab === 'candidates' && 'Upload and manage candidates'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '50vh' 
          }}>
            <div className="admin-spinner" style={{ width: 40, height: 40 }} />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'courses' && renderCoursesManagement()}
            {activeTab === 'students' && renderStudentsManagement()}
            {activeTab === 'visits' && renderCampusVisitsManagement()}
            {activeTab === 'placement' && renderPlacementBatchesManagement()}
          </>
        )}
      </main>
    </div>
  );
}

export default InstitutionDashboard;

